import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Modal, ModalBodyFooter, Button, ModalLoading, ErrorMessage, SVGIcon } from '../../../../components/custom-essentials';
import { SelectMulti, FormikControl } from '../../../../components/form';
import { TooltipWrapper } from '../../../../components/display';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Template name is required').max(100, 'Name must be 100 characters or less')
});

function BookListTemplateModal(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const { show, mode, template, books = [], onClose, createBookListTemplate, updateBookListTemplate, deleteBookListTemplate } = props;

    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Memoize book options
    const bookOptions = useMemo(() => (books || []).map(b => ({
        value: parseInt(b.id),
        label: `${b.title || b.name || 'Unknown'}${b.author ? ` - ${b.author}` : ''}`,
        obj: b
    })), [books]);

    // Get initial books for edit mode
    const getInitialBooks = useCallback(() => {
        if (!template?.items) return [];
        try {
            // items is already an array from the parent component
            const items = Array.isArray(template.items) ? template.items :
                (typeof template.items === 'string' ? JSON.parse(template.items) : []);

            return items.map(item => {
                const bookId = parseInt(item.book_id);
                const book = books.find(b => parseInt(b.id) === bookId);
                if (book) {
                    const bookTitle = book.title || book.name || 'Unknown';
                    return {
                        value: parseInt(book.id),
                        label: `${bookTitle}${book.author ? ` - ${book.author}` : ''}`,
                        obj: book
                    };
                } else {
                    return null;
                }
            }).filter(Boolean);
        } catch (e) {
            return [];
        }
    }, [template, books]);

    const handleClose = useCallback(() => onClose(false), [onClose]);

    const handleSubmit = useCallback(async (values) => {
        setLoading(true);
        try {
            const templateItems = (values.selectedBooks || []).map((b, index) => ({
                bookId: b.value,
                sortOrder: index + 1
            }));

            const baseTemplatePayload = {
                name: values.name,
                notes: values.description || '',
                active: template?.active ?? 1
            };

            if (mode === 'create') {
                const response = await createBookListTemplate({
                    template: { ...baseTemplatePayload, active: 1 },
                    templateItems
                });
                if (!response || (response.status && response.status >= 400)) {
                    throw new Error(response?.data?.message || 'Failed to create template');
                }
            } else if (mode === 'edit') {
                if (!template?.id) throw new Error('Missing template ID for edit');
                const response = await updateBookListTemplate({
                    template: { ...baseTemplatePayload, id: template.id },
                    templateItems
                });
                if (!response || (response.status && response.status >= 400)) {
                    throw new Error(response?.data?.message || 'Failed to update template');
                }
            } else if (mode === 'delete') {
                if (!template?.id) throw new Error('Missing template ID for delete');
                const response = await deleteBookListTemplate({ id: template.id });
                if (!response || (response.status && response.status >= 400)) {
                    throw new Error(response?.data?.message || 'Failed to delete template');
                }
            }

            setSubmitted(true);
            setTimeout(() => onClose(true), 1000);
        } catch (error) {
            // Error handling - modal will remain open for user to retry
        } finally {
            setLoading(false);
        }
    }, [mode, template, onClose, createBookListTemplate, updateBookListTemplate, deleteBookListTemplate]);

    // useFormik hook must be called unconditionally
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: template?.name || '',
            description: template?.notes || template?.description || '', // Backend uses 'notes', but we display as 'description'
            selectedBooks: getInitialBooks()
        },
        validationSchema: validationSchema,
        onSubmit: handleSubmit
    });

    // Early return after all hooks
    if (!show) return null;

    if (mode === 'delete') {
        return (
            <Modal show={show} onHide={handleClose}>
                <Modal.Header>
                    <h2>Delete Template</h2>
                </Modal.Header>
                <ModalBodyFooter>
                    <Modal.Body>
                        {submitted ? (
                            <h4>Template deleted successfully!</h4>
                        ) : (
                            <div>
                                <p>Are you sure you want to delete this template?</p>
                                <p className="font-semibold mt-2">{template?.name}</p>
                                <p className="text-mpLRed mt-2">This action cannot be undone.</p>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {!submitted && (
                            <div className="flex flex-row gap-x-2">
                                <Button color="lte-mpLRed" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button
                                    color="lte-mpLBlue"
                                    onClick={() => handleSubmit({})}
                                    disabled={loading}
                                >
                                    {loading ? 'Deleting...' : 'Delete'}
                                </Button>
                            </div>
                        )}
                        {submitted && (
                            <Button color="lte-mpLRed" onClick={handleClose}>
                                Close
                            </Button>
                        )}
                    </Modal.Footer>
                </ModalBodyFooter>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={handleClose} className="w-8/12">
            <Modal.Header>
                <h2>{mode === 'create' ? 'New Template' : 'Edit Template'}</h2>
            </Modal.Header>
            <ModalBodyFooter>
                <Modal.Body>
                    {loading && <ModalLoading />}
                    {submitted ? (
                        <h4>Template {mode === 'create' ? 'created' : 'updated'} successfully!</h4>
                    ) : (
                        <div className="grid grid-cols-1 gap-y-4">
                            {/* Template Name */}
                            <div>
                                <h4>Template Name *</h4>
                                <FormikControl
                                    id="template-name"
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    placeholder="Enter template name"
                                />
                                {formik.errors.name && formik.touched.name && (
                                    <ErrorMessage color="mpLRed">{formik.errors.name}</ErrorMessage>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <h4>Description</h4>
                                <FormikControl
                                    id="template-description"
                                    name="description"
                                    as="textarea"
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    placeholder="Enter description (optional)"
                                />
                            </div>

                            {/* Books Selection */}
                            <div>
                                <h4>Books in Template</h4>
                                <SelectMulti
                                    id="template-books"
                                    name="selectedBooks"
                                    value={formik.values.selectedBooks}
                                    options={bookOptions}
                                    onChange={(e) => {
                                        const value = e.target.value || [];
                                        formik.setFieldValue('selectedBooks', value);
                                    }}
                                    placeholder="Select books to include..."
                                />
                                <p className="text-sm text-gray-500 mt-1">
                                    {formik.values.selectedBooks.length} book(s) selected
                                </p>
                            </div>

                            {/* Selected Books List */}
                            {formik.values.selectedBooks.length > 0 && (
                                <div>
                                    <h4>Selected Books Order</h4>
                                    <div className="border rounded p-2 max-h-48 overflow-y-auto">
                                        {formik.values.selectedBooks.map((book, index) => (
                                            <div
                                                key={book.value}
                                                className="flex items-center justify-between py-2 border-b last:border-b-0 cursor-move hover:bg-gray-50 transition-colors"
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('text/plain', index.toString());
                                                    e.currentTarget.style.opacity = '0.5';
                                                }}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.dataTransfer.dropEffect = 'move';
                                                    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                                    if (dragIndex !== index && !isNaN(dragIndex)) {
                                                        e.currentTarget.style.borderTop = '2px solid #3b82f6';
                                                    }
                                                }}
                                                onDragLeave={(e) => {
                                                    e.currentTarget.style.borderTop = '';
                                                }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.currentTarget.style.opacity = '1';
                                                    e.currentTarget.style.borderTop = '';
                                                    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                                    const dropIndex = index;

                                                    if (!isNaN(dragIndex) && dragIndex !== dropIndex) {
                                                        const newBooks = [...formik.values.selectedBooks];
                                                        const [removed] = newBooks.splice(dragIndex, 1);
                                                        newBooks.splice(dropIndex, 0, removed);
                                                        formik.setFieldValue('selectedBooks', newBooks);
                                                    }
                                                }}
                                                onDragEnd={(e) => {
                                                    e.currentTarget.style.opacity = '1';
                                                    e.currentTarget.style.borderTop = '';
                                                }}
                                            >
                                                <div className="flex items-center gap-2 flex-1">
                                                    <SVGIcon fileName="original/Sort1" className="text-gray-400 w-4 h-4" />
                                                    <span className="text-sm">
                                                        {index + 1}. {book.label}
                                                    </span>
                                                </div>
                                                <TooltipWrapper tooltipText="Remove book">
                                                    <Button
                                                        color="lte-mpLRed"
                                                        variant="icon"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const newBooks = formik.values.selectedBooks.filter(b => b.value !== book.value);
                                                            formik.setFieldValue('selectedBooks', newBooks);
                                                        }}
                                                    >
                                                        <SVGIcon fileName="x" />
                                                    </Button>
                                                </TooltipWrapper>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {!submitted && (
                        <div className="flex flex-row gap-x-2">
                            <Button color="lte-mpLRed" onClick={handleClose}>
                                Close
                            </Button>
                            <Button
                                color="lte-mpLBlue"
                                onClick={formik.handleSubmit}
                                disabled={formik.isSubmitting}
                            >
                                {loading ? 'Saving...' : 'Submit'}
                            </Button>
                        </div>
                    )}
                    {submitted && (
                        <Button color="lte-mpLRed" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </Modal.Footer>
            </ModalBodyFooter>
        </Modal>
    );
}

export default BookListTemplateModal;
